import React from 'react';
import { Clock, Timer, Calendar, UserX, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModernModuleDashboard from '../../shared/components/dashboard/ModernModuleDashboard';
import {
  buildDashboardCharts,
  pipelineFromKpis,
  statusFromChartRows,
  withKpiVariants,
} from '../../shared/components/dashboard/moduleDashboardCharts';

export default function AttendanceDashboardPage() {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.pointage.pages.dashboardPage.${key}`, opts);

  return (
    <ModernModuleDashboard
      apiEndpoint="/timetracking/dashboard"
      title={p('title', { defaultValue: 'لوحة الحضور والانصراف' })}
      subtitle={p('subtitle', { defaultValue: 'مؤشرات الحضور والتأخير والساعات الإضافية' })}
      icon={Clock}
      accent="from-sky-600 to-blue-900"
      recentTitle={p('recent_clockins', { defaultValue: 'آخر تسجيلات' })}
      quickLinks={[
        { label: p('link_daily', { defaultValue: 'الحضور اليومي' }), href: '/timetracking/daily', icon: Clock },
        { label: p('link_overtime', { defaultValue: 'الساعات الإضافية' }), href: '/timetracking/overtime', icon: Timer },
        { label: p('link_schedules', { defaultValue: 'جداول العمل' }), href: '/pointage/horaires-travail', icon: Calendar },
        { label: p('link_analytics', { defaultValue: 'التحليلات' }), href: '/timetracking/analyses-rapports', icon: Clock },
      ]}
      mapData={(data) => {
        const kpis = data.kpis || {};
        const chartRows = data.chartRows || [];
        const { active, inactive } = statusFromChartRows(chartRows, [
          'present',
          'checked_in',
          'on_time',
        ]);

        return {
          hero: {
            label: p('kpi_present', { defaultValue: 'حاضرون اليوم' }),
            value: kpis.presentToday ?? 0,
            icon: Clock,
          },
          kpis: withKpiVariants([
            { label: p('kpi_late', { defaultValue: 'تأخيرات' }), value: kpis.lateArrivals ?? 0, icon: Timer },
            { label: p('kpi_overtime', { defaultValue: 'ساعات إضافية (أسبوع)' }), value: kpis.overtimeHours ?? 0, icon: Calendar },
            { label: p('kpi_absent', { defaultValue: 'غائبون' }), value: kpis.absentees ?? 0, icon: UserX },
            {
              label: p('kpi_total_tracked', { defaultValue: 'إجمالي المتابَعين' }),
              value: (kpis.presentToday ?? 0) + (kpis.absentees ?? 0) + (kpis.lateArrivals ?? 0),
              icon: Users,
            },
          ]),
          charts: buildDashboardCharts({
            pipeline: {
              title: p('chart_attendance', { defaultValue: 'حالة الحضور اليوم' }),
              subtitle: p('chart_pipeline_subtitle', { defaultValue: 'حضور وتأخير وغياب' }),
              alwaysShow: true,
              rows: pipelineFromKpis([
                { label: p('kpi_present', { defaultValue: 'حاضرون اليوم' }), value: kpis.presentToday ?? 0 },
                { label: p('kpi_late', { defaultValue: 'تأخيرات' }), value: kpis.lateArrivals ?? 0 },
                { label: p('kpi_absent', { defaultValue: 'غائبون' }), value: kpis.absentees ?? 0 },
              ]),
            },
            status: {
              title: p('chart_status_title', { defaultValue: 'حضور اليوم' }),
              subtitle: p('chart_status_subtitle', { defaultValue: 'حاضرون مقابل غائبون' }),
              active: active || kpis.presentToday || 0,
              inactive: inactive || kpis.absentees || 0,
              alwaysShow: true,
            },
            ranked: {
              title: p('chart_ranked_title', { defaultValue: 'توزيع حالات الحضور' }),
              subtitle: p('chart_ranked_subtitle', { defaultValue: 'حسب الحالة' }),
              rows: chartRows,
            },
          }),
          recentItems: data.recentItems || [],
        };
      }}
    />
  );
}
